import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pill, Ambulance, Package, Stethoscope, Heart, TestTube } from 'lucide-react';

const SERVICE_BOXES: Array<{
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string | null;
  available: boolean;
}> = [
  {
    id: 'medicine',
    title: 'Medicine Service',
    description: 'View vendor ledger for medicine orders — MRP, discount, delivery charges, commission & settlements',
    icon: <Pill className="w-10 h-10 text-primary-600 dark:text-primary-400" />,
    path: '/accountant/ledger/vendor/medicine',
    available: true,
  },
  {
    id: 'ambulance',
    title: 'Ambulance Service',
    description: 'View vendor ledger for ambulance bookings — gross amount, commission & settlements',
    icon: <Ambulance className="w-10 h-10 text-primary-600 dark:text-primary-400" />,
    path: '/accountant/ledger/vendor/ambulance',
    available: true,
  },
  {
    id: 'placeholder3',
    title: 'Coming Soon',
    description: 'More vendor types will be added here.',
    icon: <Package className="w-10 h-10 text-gray-400 dark:text-gray-500" />,
    path: null,
    available: false,
  },
  {
    id: 'doctor',
    title: 'Doctor Consultation Service',
    description: 'View vendor ledger for doctor consultations — gross amount, commission & settlements',
    icon: <Stethoscope className="w-10 h-10 text-primary-600 dark:text-primary-400" />,
    path: '/accountant/ledger/vendor/doctor',
    available: true,
  },
  {
    id: 'placeholder5',
    title: 'Coming Soon',
    description: 'More vendor types will be added here.',
    icon: <Heart className="w-10 h-10 text-gray-400 dark:text-gray-500" />,
    path: null,
    available: false,
  },
  {
    id: 'placeholder6',
    title: 'Coming Soon',
    description: 'More vendor types will be added here.',
    icon: <TestTube className="w-10 h-10 text-gray-400 dark:text-gray-500" />,
    path: null,
    available: false,
  },
];

const VendorLedgerPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Vendor Ledger</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Select a service type to view vendor-wise ledger entries
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICE_BOXES.map((box) => (
          <div
            key={box.id}
            onClick={() => box.available && box.path && navigate(box.path)}
            className={`
              rounded-xl border-2 p-6 transition-all
              ${box.available
                ? 'border-primary-200 dark:border-primary-800 bg-white dark:bg-gray-800 hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-lg cursor-pointer'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed opacity-75'
              }
            `}
          >
            <div className="flex items-start gap-4">
              <div
                className={`flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center ${
                  box.available
                    ? 'bg-primary-50 dark:bg-primary-900/30'
                    : 'bg-gray-100 dark:bg-gray-700'
                }`}
              >
                {box.icon}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {box.title}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {box.description}
                </p>
                {box.available && (
                  <span className="inline-block mt-3 text-sm font-medium text-primary-600 dark:text-primary-400">
                    View ledger
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VendorLedgerPage;
